import React, { useCallback, useMemo } from 'react';

import { Platform, Pressable, StyleProp, View, ViewStyle } from 'react-native';

import { useTranslation } from 'react-i18next';

import { RightArrowSettings } from 'assets/images';

import { LANGUAGE_KEYS } from 'config/languageKeys';
import { PROFILE_AGE_RATING_BUTTON } from 'config/testID';

import isFunction from 'utils/function';
import getPressableOpacityStyles from 'utils/getPressableOpacityStyles';
import { isWeb } from 'utils/responsiveness';

import Text, { ETextCategory, ETextColor } from 'components/Text';

import styles from './styles';

type Props = {
    title: string;
    subtitle?: string;
    disabled?: boolean;
    onPress?(): void;
    style?: StyleProp<ViewStyle>;
};

const {
    ACCESSIBILITY: {
        ACCESSIBILITY_GENERAL_TAPP_TWICE,
        ACCESSIBILITY_GENERAL_TAPP_TWICE_WEB,
    },
} = LANGUAGE_KEYS;

const ProfileRowItem = ({
    title,
    subtitle,
    disabled = false,
    onPress,
    style,
}: Props) => {
    const { t } = useTranslation();

    const handlePress = useCallback(() => {
        if (isFunction(onPress)) {
            onPress();
        }
    }, [onPress]);

    const accessibilityLabel = useMemo(
        () =>
            `${title}  ${subtitle} ${
                isWeb ? t(ACCESSIBILITY_GENERAL_TAPP_TWICE_WEB) : ''
            }`,
        [t],
    );

    return (
        <Pressable
            disabled={disabled}
            onPress={handlePress}
            style={getPressableOpacityStyles}
            testID={PROFILE_AGE_RATING_BUTTON}
            accessibilityLabel={accessibilityLabel}
            accessibilityRole="button"
            {...(Platform.OS === 'ios' && {
                accessibilityHint: t(ACCESSIBILITY_GENERAL_TAPP_TWICE),
            })}
        >
            <View style={[styles.container, style]}>
                <View style={styles.titleContainer}>
                    <Text
                        style={styles.title}
                        category={ETextCategory.body1}
                        color={ETextColor.neutral}
                    >
                        {title}
                    </Text>
                </View>
                <RightArrowSettings style={styles.arrow} />
            </View>
            {!!subtitle && (
                <Text
                    style={styles.subtitle}
                    category={ETextCategory.body2}
                    color={ETextColor.light_grey}
                >
                    {subtitle}
                </Text>
            )}
        </Pressable>
    );
};

export default ProfileRowItem;
